import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default markers in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icons for different zone types
const createCustomIcon = (color: string, type: string) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 0 20px ${color};
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      animation: pulse 2s infinite;
    ">${type === 'flood' ? '💧' : type === 'fire' ? '🔥' : '🏢'}</div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -20],
  });
};

// Component to update map view when center changes
const MapUpdater: React.FC<{ center: [number, number] }> = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    map.setView(center, map.getZoom());
  }, [center, map]);
  return null;
};

interface Zone {
  name: string;
  status: 'critical' | 'warning' | 'monitor';
  trapped: number;
  timeRemaining: number;
  coordinates: [number, number];
  type: 'flood' | 'fire' | 'earthquake';
  description?: string;
}

interface CrisisMapProps {
  center?: [number, number];
  zoom?: number;
  zones?: Zone[];
  onZoneClick?: (zone: Zone) => void;
}

const CrisisMap: React.FC<CrisisMapProps> = ({ 
  center = [12.9716, 77.5946], // Default: Bangalore
  zoom = 12,
  zones = [],
  onZoneClick 
}) => {
  const [mapReady, setMapReady] = useState(false);

  // Default zones if none provided
  const defaultZones: Zone[] = [
    {
      name: 'ZONE A',
      status: 'critical',
      trapped: 47,
      timeRemaining: 25,
      coordinates: [12.935, 77.615],
      type: 'flood',
      description: 'Severe flooding - Immediate evacuation needed'
    },
    {
      name: 'ZONE B',
      status: 'warning',
      trapped: 12,
      timeRemaining: 120,
      coordinates: [12.9716, 77.5946],
      type: 'fire',
      description: 'Fire spreading - Deploy fire trucks'
    },
    {
      name: 'ZONE C',
      status: 'monitor',
      trapped: 3,
      timeRemaining: 240,
      coordinates: [13.005, 77.57],
      type: 'earthquake',
      description: 'Structural damage - Monitor closely'
    },
    {
      name: 'ZONE D',
      status: 'warning',
      trapped: 8,
      timeRemaining: 90,
      coordinates: [12.95, 77.64],
      type: 'flood',
      description: 'Water rising - Prepare evacuation'
    },
    {
      name: 'ZONE E',
      status: 'monitor',
      trapped: 2,
      timeRemaining: 300,
      coordinates: [12.99, 77.62],
      type: 'earthquake',
      description: 'Aftershocks reported - Stay alert'
    }
  ];

  const displayZones = zones.length > 0 ? zones : defaultZones;

  // Get color based on status
  const getStatusColor = (status: string) => {
    switch(status) {
      case 'critical': return '#ff0000';
      case 'warning': return '#ff6600';
      case 'monitor': return '#00ffff';
      default: return '#0066ff';
    }
  };

  // Get radius based on trapped count
  const getRadius = (trapped: number) => {
    return Math.max(200, trapped * 20);
  };

  return (
    <div className="relative w-full h-full rounded-xl overflow-hidden border-2 border-cyan-500/30">
      <MapContainer
        center={center}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        whenReady={() => setMapReady(true)}
        className="z-0"
      >
        {/* Dark theme tile layer */}
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        
        {/* Heat overlay layer - Using free OpenStreetMap instead */}
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          opacity={0.3}
/>

        {/* Zone markers and circles */}
        {displayZones.map((zone, index) => {
          const color = getStatusColor(zone.status);
          const radius = getRadius(zone.trapped);
          
          return (
            <React.Fragment key={index}>
              {/* Pulsing circle for affected area */}
              <Circle
                center={zone.coordinates}
                radius={radius}
                pathOptions={{
                  color: color,
                  fillColor: color,
                  fillOpacity: 0.15,
                  weight: 2,
                  opacity: 0.6
                }}
              />
              
              {/* Animated inner circle */}
              <Circle
                center={zone.coordinates}
                radius={radius * 0.3}
                pathOptions={{
                  color: color,
                  fillColor: color,
                  fillOpacity: 0.3,
                  weight: 3,
                  opacity: 0.8,
                  className: 'animate-pulse'
                }}
              />
              
              {/* Custom marker */}
              <Marker
                position={zone.coordinates}
                icon={createCustomIcon(color, zone.type)}
                eventHandlers={{
                  click: () => onZoneClick && onZoneClick(zone)
                }}
              >
                <Popup className="cyber-popup">
                  <div className="bg-gray-900 p-4 rounded-lg border border-cyan-500">
                    <h3 className="text-lg font-bold" style={{ color }}>{zone.name}</h3>
                    <p className="text-sm mt-2">{zone.description || `${zone.type.toUpperCase()} disaster`}</p>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div className="bg-black/50 p-2 rounded">
                        <span className="text-xs text-gray-400">Trapped</span>
                        <p className="text-xl font-bold" style={{ color }}>{zone.trapped}</p>
                      </div>
                      <div className="bg-black/50 p-2 rounded">
                        <span className="text-xs text-gray-400">Time Left</span>
                        <p className="text-xl font-bold" style={{ color }}>{zone.timeRemaining}m</p>
                      </div>
                    </div>
                    <button 
                      className="cyber-button text-xs w-full mt-3 py-2"
                      onClick={() => onZoneClick && onZoneClick(zone)}
                    >
                      DEPLOY RESOURCES
                    </button>
                  </div>
                </Popup>
              </Marker>
            </React.Fragment>
          );
        })}
        
        <MapUpdater center={center} />
      </MapContainer>

      {/* Map overlay controls */}
      <div className="absolute top-4 right-4 z-10 flex flex-col gap-2">
        <button className="bg-black/70 hover:bg-black/90 text-white p-2 rounded-lg border border-cyan-500/50">
          <span className="text-xl">📍</span>
        </button>
        <button className="bg-black/70 hover:bg-black/90 text-white p-2 rounded-lg border border-cyan-500/50">
          <span className="text-xl">🔍</span>
        </button>
        <button className="bg-black/70 hover:bg-black/90 text-white p-2 rounded-lg border border-cyan-500/50">
          <span className="text-xl">🌡️</span>
        </button>
      </div>

      {/* Legend */}
      <div className="absolute bottom-4 left-4 z-10 bg-black/70 p-3 rounded-lg border border-cyan-500/30">
        <div className="text-xs text-gray-400 mb-2">ZONE STATUS</div>
        <div className="flex gap-3">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            <span className="text-xs">Critical</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
            <span className="text-xs">Warning</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-cyan-500 rounded-full"></div>
            <span className="text-xs">Monitor</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrisisMap;